/** B2 — Libro de gastos: facturas recibidas del emisor con libros del autónomo. */
import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Download, Loader2, Paperclip, Pencil, Plus, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { downloadFile } from '../../lib/download'
import { formatCents } from '../../lib/money'
import {
  EXPENSE_CATEGORY_LABEL,
  EXPENSE_KIND_LABEL,
  type ExpenseDto,
  type ExpenseListResponse,
} from '@toga/shared'
import ExpenseModal from './ExpenseModal'
import { Note, rateLabel, selectCls, td, tdR, th, thR } from './common'

const PERIODS: [string, string][] = [
  ['', 'Todo el año'],
  ['q1', '1.er trimestre'],
  ['q2', '2.º trimestre'],
  ['q3', '3.er trimestre'],
  ['q4', '4.º trimestre'],
]

export default function ExpensesTab({ issuerId, year }: { issuerId: string; year: number }) {
  const [period, setPeriod] = useState('')
  const [kind, setKind] = useState('')
  const [category, setCategory] = useState('')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [data, setData] = useState<ExpenseListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const [busy, setBusy] = useState(false)
  const [modal, setModal] = useState<{ expense: ExpenseDto | null } | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250)
    return () => clearTimeout(t)
  }, [q])

  const qs = useMemo(() => {
    const p = new URLSearchParams({ year: String(year), issuerId })
    if (period) p.set('quarter', period.slice(1))
    if (kind) p.set('kind', kind)
    if (category) p.set('category', category)
    if (debouncedQ) p.set('q', debouncedQ)
    return p.toString()
  }, [year, issuerId, period, kind, category, debouncedQ])

  useEffect(() => {
    let alive = true
    setError(null)
    api
      .get<ExpenseListResponse>(`/expenses?${qs}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [qs, tick])

  const reload = () => setTick((t) => t + 1)

  const exportCsv = async () => {
    setBusy(true)
    try {
      await downloadFile(`/expenses.csv?${qs}`, `libro-gastos-${year}.csv`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo exportar')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (e: ExpenseDto) => {
    if (!confirm(`¿Eliminar la factura de ${e.supplierName} (${formatCents(e.totalCents)})?`))
      return
    try {
      await api.delete(`/expenses/${e.id}`)
      reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? 'No se pudo eliminar')
    }
  }

  const openAttachment = async (e: ExpenseDto) => {
    try {
      const r = await api.get<{ url: string }>(`/expenses/${e.id}/attachment`)
      window.open(r.url, '_blank', 'noopener')
    } catch {
      setError('No se pudo abrir el justificante')
    }
  }

  return (
    <div>
      <div className="px-5 py-3 border-b border-base flex flex-wrap items-center gap-2">
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className={selectCls}
          aria-label="Periodo"
        >
          {PERIODS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className={selectCls}
          aria-label="Tipo"
        >
          <option value="">Gastos e inversiones</option>
          {Object.entries(EXPENSE_KIND_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={`${selectCls} max-w-[16rem]`}
          aria-label="Categoría"
        >
          <option value="">Todas las categorías</option>
          {Object.entries(EXPENSE_CATEGORY_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Proveedor, NIF, n.º de factura…"
          className={`${selectCls} w-56`}
          aria-label="Buscar"
        />
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void exportCsv()}
            className="px-3 py-1.5 rounded-lg text-xs t-secondary border border-base hover:bg-hover inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={13} /> CSV para la gestoría
          </button>
          <button
            type="button"
            onClick={() => setModal({ expense: null })}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga inline-flex items-center gap-1.5"
          >
            <Plus size={13} /> Anotar factura recibida
          </button>
        </div>
      </div>

      {error && (
        <div className="m-4 text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}
      {!data && !error && (
        <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      )}
      {data && data.rows.length === 0 && (
        <div className="px-5 py-10 text-center t-muted text-sm">
          Aún no hay facturas recibidas en este periodo. Anota la primera con «Anotar factura
          recibida»: puedes subir el PDF o una foto y Toga la lee.
        </div>
      )}
      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-base">
                <th className={th}>Fecha</th>
                <th className={th}>Proveedor</th>
                <th className={th}>Categoría</th>
                <th className={thR}>Base</th>
                <th className={thR}>IVA</th>
                <th className={thR}>Total</th>
                <th className={thR}>Deducible (IRPF)</th>
                <th className={thR}>IVA deducible</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((e) => (
                <tr key={e.id} className="border-b border-base/50 hover:bg-hover/40">
                  <td className={`${td} whitespace-nowrap`}>
                    {new Date(e.issueDate).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
                  </td>
                  <td className={td}>
                    <div className="t-primary">{e.supplierName}</div>
                    <div className="text-[11px] t-faint">
                      {[e.supplierNif, e.invoiceNumber].filter(Boolean).join(' · ')}
                      {e.description ? ` — ${e.description}` : ''}
                    </div>
                  </td>
                  <td className={td}>
                    <span className="text-xs">{EXPENSE_CATEGORY_LABEL[e.category]}</span>
                    {e.kind === 'INVERSION' && (
                      <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full border border-toga-500/40 text-toga-300">
                        inversión · {e.usefulLifeYears} años
                      </span>
                    )}
                  </td>
                  <td className={tdR}>{formatCents(e.baseCents)}</td>
                  <td className={tdR}>
                    {formatCents(e.vatCents)}
                    <span className="block text-[10px] t-faint">{rateLabel(e.vatRate)}</span>
                  </td>
                  <td className={`${tdR} font-semibold`}>{formatCents(e.totalCents)}</td>
                  <td className={tdR}>
                    {e.kind === 'INVERSION' ? (
                      <span className="t-faint" title="Se deduce su amortización">
                        amortiza
                      </span>
                    ) : (
                      <>
                        {formatCents(e.deductibleBaseCents)}
                        {e.deductiblePct < 100 && (
                          <span className="block text-[10px] t-faint">{e.deductiblePct} %</span>
                        )}
                      </>
                    )}
                  </td>
                  <td className={tdR}>
                    {formatCents(e.deductibleVatCents)}
                    {e.vatDeductiblePct < 100 && (
                      <span className="block text-[10px] t-faint">{e.vatDeductiblePct} %</span>
                    )}
                  </td>
                  <td className={`${td} whitespace-nowrap text-right`}>
                    {e.hasAttachment && (
                      <button
                        type="button"
                        onClick={() => void openAttachment(e)}
                        className="p-1 t-muted hover:text-toga-300"
                        title={`Justificante: ${e.attachmentName ?? ''}`}
                        aria-label="Ver justificante"
                      >
                        <Paperclip size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setModal({ expense: e })}
                      className="p-1 t-muted hover:text-toga-300"
                      aria-label="Editar"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(e)}
                      className="p-1 t-muted hover:text-rose-300"
                      aria-label="Eliminar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-base font-semibold">
                <td className={td} colSpan={3}>
                  {data.totals.count} facturas
                </td>
                <td className={tdR}>{formatCents(data.totals.baseCents)}</td>
                <td className={tdR}>{formatCents(data.totals.vatCents)}</td>
                <td className={tdR}>{formatCents(data.totals.totalCents)}</td>
                <td className={tdR}>{formatCents(data.totals.deductibleBaseCents)}</td>
                <td className={tdR}>{formatCents(data.totals.deductibleVatCents)}</td>
                <td className={td} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {data?.truncated && (
        <p className="px-5 py-2 text-xs text-amber-300">
          Hay más facturas de las que caben aquí: acota el periodo.
        </p>
      )}
      <Note>
        Las facturas se reparten por trimestre según su fecha. «Deducible» aplica el porcentaje que
        indiques (uso mixto); un bien de inversión no es gasto de golpe: se deduce su amortización
        (pestaña Inversiones). Pasa el CSV a tu gestoría.
      </Note>

      {modal && (
        <ExpenseModal
          issuerId={issuerId}
          expense={modal.expense}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null)
            reload()
          }}
        />
      )}
    </div>
  )
}
